/* eslint-disable react/require-default-props */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { FlexColumn, Textarea, Typography } from 'design-system';
import { allowedMimeTypes } from '../../../../helpers/mimeTypes';
import {
  choiceAnswerNeedsImage,
  choiceAnswerNeedsWriteIn,
} from '../../helpers/clientConnectSurveyQuestions';

function normalizeImages(value) {
  if (!value) {
    return [];
  }
  return Array.isArray(value) ? value : [value];
}

export default function ChoiceOptionExtras({
  question = null,
  value = '',
  writeIn = '',
  images = [],
  onWriteInChange,
  onImagesChange,
  interactive = false,
}) {
  const intl = useIntl();
  const inputRef = useRef(null);
  const needsWriteIn = choiceAnswerNeedsWriteIn(question, value);
  const needsImage = choiceAnswerNeedsImage(question, value);
  const imageFiles = normalizeImages(images);
  const [previewUrls, setPreviewUrls] = useState({});

  useEffect(() => {
    const files = normalizeImages(images).filter((file) => file.type?.startsWith('image/'));
    const next = {};
    files.forEach((file) => {
      const key = `${file.name}-${file.size}`;
      next[key] = URL.createObjectURL(file);
    });
    setPreviewUrls(next);
    return () => {
      Object.values(next).forEach((url) => URL.revokeObjectURL(url));
    };
  }, [images]);

  if (!needsWriteIn && !needsImage) {
    return null;
  }

  const handleImageInput = (event) => {
    if (!interactive) {
      return;
    }

    const selected = Array.from(event.target.files || []);
    event.target.value = '';

    if (!selected.length) {
      return;
    }

    const accepted = selected.filter((file) => allowedMimeTypes.includes(file.type));
    if (!accepted.length) {
      return;
    }

    const merged = [...imageFiles];
    accepted.forEach((file) => {
      const isDuplicate = merged.some((item) => item.name === file.name && item.size === file.size);
      if (!isDuplicate) {
        merged.push(file);
      }
    });

    onImagesChange?.(merged);
  };

  return (
    <FlexColumn align="stretch" fullWidth className="cc-preview-choice-extras">
      {needsWriteIn ? (
        <Textarea
          rows={3}
          className="cc-preview-choice-extras__write-in"
          placeholder={intl.formatMessage({ id: 'enter-your-comment' })}
          value={writeIn ?? ''}
          readOnly={!interactive}
          onChange={(event) => onWriteInChange?.(event.target.value)}
        />
      ) : null}

      {needsImage ? (
        <FlexColumn align="stretch" fullWidth className="cc-preview-choice-extras__image">
          <input
            ref={inputRef}
            type="file"
            className="cc-preview-choice-extras__image-input"
            accept={allowedMimeTypes.join(',')}
            multiple
            disabled={!interactive}
            onChange={handleImageInput}
          />
          <button
            type="button"
            className="cc-preview-choice-extras__upload-btn"
            disabled={!interactive}
            onClick={() => inputRef.current?.click()}
          >
            <Typography component="span" variant="bodyMedium">
              {intl.formatMessage({ id: 'upload-image' })}
            </Typography>
          </button>

          {imageFiles.length ? (
            <FlexColumn align="stretch" fullWidth className="cc-preview-choice-extras__image-list">
              {imageFiles.map((file) => (
                <FlexColumn
                  key={`${file.name}-${file.size}`}
                  align="stretch"
                  fullWidth
                  className="cc-preview-choice-extras__image-item"
                >
                  {file.type?.startsWith('image/') ? (
                    <img
                      src={previewUrls[`${file.name}-${file.size}`]}
                      alt={file.name}
                      className="cc-preview-choice-extras__image-preview"
                    />
                  ) : (
                    <Typography component="span" variant="bodySmall">
                      {file.name}
                    </Typography>
                  )}
                  {interactive ? (
                    <button
                      type="button"
                      className="cc-preview-choice-extras__remove-btn"
                      onClick={() => {
                        const next = imageFiles.filter(
                          (item) => item.name !== file.name || item.size !== file.size
                        );
                        onImagesChange?.(next);
                      }}
                    >
                      {intl.formatMessage({ id: 'remove', defaultMessage: 'Remove' })}
                    </button>
                  ) : null}
                </FlexColumn>
              ))}
            </FlexColumn>
          ) : null}
        </FlexColumn>
      ) : null}
    </FlexColumn>
  );
}

ChoiceOptionExtras.propTypes = {
  question: PropTypes.shape({
    options: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.arrayOf(PropTypes.string)]),
  writeIn: PropTypes.string,
  images: PropTypes.arrayOf(PropTypes.shape({})),
  onWriteInChange: PropTypes.func,
  onImagesChange: PropTypes.func,
  interactive: PropTypes.bool,
};
